import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Copy, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { DashboardShell, PlaceholderCard } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import {
  DEFAULT_TEMPLATES,
  fetchTemplates,
  renderTemplate,
  type MessageTemplate,
} from "@/lib/messages";

export const Route = createFileRoute("/_authenticated/partner/messages")({
  head: () => ({
    meta: [
      { title: "Business messages · Landoo Partner" },
      {
        name: "description",
        content: "Send professional messages to customers using reusable templates.",
      },
    ],
  }),
  component: MessagesPage,
});

const inp =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30";

function MessagesPage() {
  const { user, profile } = useAuth();
  const [saved, setSaved] = useState<MessageTemplate[]>([]);
  const [slug, setSlug] = useState("welcome");
  const [vars, setVars] = useState({ customer: "", listing: "", amount: "", date: "" });

  useEffect(() => {
    if (!user) return;
    fetchTemplates(user.id)
      .then(setSaved)
      .catch(() => setSaved([]));
  }, [user?.id]);

  const templates = useMemo(() => {
    return DEFAULT_TEMPLATES.map((d) => {
      const override = saved.find((s) => s.slug === d.slug);
      return override ? { ...d, name: override.name, body: override.body } : d;
    });
  }, [saved]);

  const active = templates.find((t) => t.slug === slug) ?? templates[0];
  const preview = renderTemplate(active.body, {
    ...vars,
    business: profile?.full_name ?? undefined,
  });

  const copy = async () => {
    await navigator.clipboard.writeText(preview);
    toast.success("Message copied");
  };

  return (
    <DashboardShell title="Business messages" role="Partner" items={PARTNER_NAV}>
      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        <div className="rounded-2xl border border-border bg-card p-3 shadow-soft">
          <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <MessagesSquare className="h-3.5 w-3.5" /> Templates
          </div>
          <div className="flex flex-col gap-1">
            {templates.map((t) => (
              <button
                key={t.slug}
                onClick={() => setSlug(t.slug)}
                className={`rounded-xl px-3 py-2 text-left text-xs font-medium transition ${
                  slug === t.slug
                    ? "bg-brown text-primary-foreground"
                    : "text-brown-dark hover:bg-brand-soft"
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Customer name">
                <input
                  className={inp}
                  value={vars.customer}
                  onChange={(e) => setVars({ ...vars, customer: e.target.value })}
                />
              </Field>
              <Field label="Listing / parking name">
                <input
                  className={inp}
                  value={vars.listing}
                  onChange={(e) => setVars({ ...vars, listing: e.target.value })}
                />
              </Field>
              <Field label="Amount">
                <input
                  className={inp}
                  value={vars.amount}
                  onChange={(e) => setVars({ ...vars, amount: e.target.value })}
                />
              </Field>
              <Field label="Date">
                <input
                  className={inp}
                  value={vars.date}
                  onChange={(e) => setVars({ ...vars, date: e.target.value })}
                />
              </Field>
            </div>

            <div className="mt-4">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Message preview
              </div>
              <div className="whitespace-pre-wrap rounded-xl border border-border bg-cream/50 p-3 text-sm text-brown-dark">
                {preview}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-xl bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft"
              >
                <Copy className="h-4 w-4" /> Copy message
              </button>
              <button
                onClick={() => toast.info("Direct WhatsApp, SMS and email sending is coming soon")}
                className="rounded-xl border border-border bg-background px-4 py-2 text-xs font-semibold text-brown-dark"
              >
                Send via WhatsApp / SMS / Email
              </button>
            </div>
          </div>

          <PlaceholderCard
            title="Message history"
            description="Sent message history and delivery status will appear here once messaging channels are connected."
          />
        </div>
      </div>
    </DashboardShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </label>
  );
}
